import { shortName } from '../tasks/taskUi';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "3.1 GB", "240 MB", "80 MB": one decimal only under 10. */
export const formatSize = (n) => {
    if (n == null) return '';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = n;
    let u = 0;
    while (v >= 1024 && u < units.length - 1) {
        v /= 1024;
        u++;
    }
    return `${u === 0 || v >= 10 ? Math.round(v) : v.toFixed(1).replace(/\.0$/, '')} ${units[u]}`;
};

/** "today", "yesterday", "2 days ago", then "12 Nov". */
export const dayAgo = (iso, now = new Date()) => {
    if (!iso) return '';
    const d = new Date(iso);
    const days = Math.round(
        (Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) - Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000,
    );
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    return `${d.getDate()} ${MONTHS[d.getMonth()]}${d.getFullYear() === now.getFullYear() ? '' : ` ${d.getFullYear()}`}`;
};

export const shortDay = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

/** "1 file", "2 files". */
export const fileCount = (n) => (n === 1 ? '1 file' : `${n} files`);

/** The Added column: "ramesh · Editing · today". */
export const addedLine = (file) => [shortName(file.uploadedBy), file.stageName, dayAgo(file.uploadedAt)].filter(Boolean);

/** "about 4 min left", from what was sent since this run started. */
export const timeLeft = (sentSinceStart, msSinceStart, remaining) => {
    if (!sentSinceStart || msSinceStart < 2000 || remaining <= 0) return null;
    const seconds = remaining / (sentSinceStart / (msSinceStart / 1000));
    if (seconds < 60) return 'less than a minute left';
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `about ${minutes} min left`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `about ${h} h${m ? ` ${m} min` : ''} left`;
};

/** "68% · 2.1 of 3.1 GB · about 4 min left". */
export const progressLine = (sent, size, left) => {
    const pct = size ? Math.floor((sent / size) * 100) : 0;
    const total = formatSize(size);
    const unit = total.split(' ')[1];
    const factor = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 }[unit];
    const done = sent / factor;
    const doneText = done >= 10 || unit === 'B' ? Math.round(done) : done.toFixed(1).replace(/\.0$/, '');
    return [`${pct}%`, `${doneText} of ${total}`, left].filter(Boolean).join(' · ');
};

export const ACTIVE = ['waiting', 'starting', 'sending', 'finishing'];

/** The tab's title, since the page sits in a background tab while people work in the task list. */
export const tabTitle = (title, uploads) => {
    const active = uploads.filter((u) => ACTIVE.includes(u.state));
    if (active.length) {
        const size = active.reduce((a, u) => a + u.size, 0);
        const sent = active.reduce((a, u) => a + (u.sent || 0), 0);
        const pct = size ? Math.floor((sent / size) * 100) : 0;
        return `${pct}% · Uploading ${fileCount(active.length)} · ${title}`;
    }
    if (uploads.some((u) => u.state === 'stopped' || u.state === 'failed')) return `Upload stopped · ${title}`;
    if (uploads.some((u) => u.state === 'done')) return `Done · ${title}`;
    return `${title} · Shared folder`;
};

/** "Uploading 2 files to Gujarati. Keep this tab open until they finish." */
export const bannerText = (uploads) => {
    const active = uploads.filter((u) => ACTIVE.includes(u.state));
    if (!active.length) return null;
    const labels = [...new Set(active.map((u) => u.folderLabel))];
    const where = labels.length < 2 ? labels[0] : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
    return `Uploading ${fileCount(active.length)} to ${where}. Keep this tab open until ${active.length === 1 ? 'it finishes' : 'they finish'}.`;
};

/** A language row's right side: "2 files · Editing", "Locked · 2 files · Completed 10 Nov". */
export const languageStatus = (folder) => {
    const n = folder.files.length;
    if (folder.lockMessage) {
        const when =
            folder.taskStatus === 'COMPLETED' ? `Completed ${shortDay(folder.completedAt)}` : folder.taskStatus === 'CANCELLED' ? 'Cancelled' : null;
        return ['Locked', fileCount(n), when].filter(Boolean).join(' · ');
    }
    return [n ? fileCount(n) : 'No files yet', folder.currentStageName].filter(Boolean).join(' · ');
};

const PROGRAMS = ['exe', 'msi', 'bat', 'cmd', 'com', 'scr', 'ps1', 'vbs', 'jar', 'app', 'dmg', 'pkg', 'sh'];

export const isProgram = (name) => PROGRAMS.includes((name.split('.').pop() || '').toLowerCase());

/** Why a file can't be added, checked before anything is sent; null when it can. */
export const refusal = (file, maxBytes) => {
    if (isProgram(file.name)) return "Programs can't be added to a shared folder.";
    if (maxBytes && file.size > maxBytes) {
        return `${formatSize(file.size)} is over the ${formatSize(maxBytes)} limit. Ask an admin if you need larger files.`;
    }
    if (file.size === 0) return 'The file is empty.';
    return null;
};

// ------------------------------------------------------------------ New Dubbing Audio from a task (6d)

/** "10:42", the time of day a run started or ended. */
export const clockTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** "Hindi_dub.wav" → "Hindi_dub_converted.wav", as the server names the result before any "(2)". */
export const convertedName = (sourceName) => {
    const dot = sourceName.lastIndexOf('.');
    return `${dot > 0 ? sourceName.slice(0, dot) : sourceName}_converted.wav`;
};

/** "at 10:42" today, "yesterday at 10:42" or "3 Nov at 10:42" before. */
export const atTime = (iso, now = new Date()) => {
    const day = dayAgo(iso, now);
    return `${day === 'today' ? '' : `${day} `}at ${clockTime(iso)}`;
};

/** The line under New Dubbing Audio in the drawer: running, done or stopped. done: whether to offer Open folder. */
export const conversionLine = (latest, languageLabel, now = new Date()) => {
    if (!latest) {
        return { text: `Makes dubbing audio straight into the ${languageLabel} shared folder, with no download or upload.` };
    }
    if (latest.status === 'PROCESSING') {
        return {
            text: `Making dubbing audio, started ${atTime(latest.startedAt, now)}. It usually takes a few minutes, and you can close this. The result goes into the ${languageLabel} folder.`,
        };
    }
    if (latest.status === 'COMPLETED') {
        return {
            text: `Dubbing audio ready ${atTime(latest.finishedAt, now)}. ${latest.resultName} is in the ${languageLabel} folder.`,
            done: true,
        };
    }
    const why = latest.stopReason ? ` ${latest.stopReason}` : '';
    return {
        text: `Dubbing audio stopped ${atTime(latest.finishedAt, now)}.${why} Nothing was saved. Try New Dubbing Audio again.`,
        error: true,
    };
};
